import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppLoading } from 'common/components/appLoading/appLoading';
import { PopupHeader } from 'common/components/PopupHeader/PopupHeader';
import { RestApi } from 'common/services/rest-api.service';
import { showSuccess, showError } from 'features/AlertSlice';
import { replaceAllRows } from 'features/GridSlice';
import { extractErrorMessage } from 'common/utils/errorHandler';
import classNames from 'classnames';
import './KeyDomainsDeletePopup.scss';

interface KeyDomainsDeletePopupProps {
  onClose: () => void;
  assets: string[];
  action: 'delete-and-blacklist' | 'delete-and-retain' | 'delete-related-names';
}

export function KeyDomainsDeletePopup(props: KeyDomainsDeletePopupProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch<any>();

  const getTitle = () => {
    if (props.action === 'delete-related-names') {
      return 'Delete Related Names';
    }
    return props.action === 'delete-and-blacklist'
      ? 'Delete and Blacklist Key Domains'
      : 'Delete and Retain Key Domains';
  };

  const getMessage = () => {
    const assetCount = props.assets.length;
    const assetText = assetCount === 1 ? 'asset' : 'assets';

    if (props.action === 'delete-related-names') {
      return `Are you sure you want to delete ${assetCount} related names ${assetText}?`;
    }

    const actionText = props.action === 'delete-and-blacklist'
      ? 'delete and blacklist'
      : 'delete and retain';

    return `Are you sure you want to ${actionText} ${assetCount} key domains ${assetText}?`;
  };

  const getButtonText = () => {
    if (props.action === 'delete-related-names') {
      return 'Delete';
    }
    return props.action === 'delete-and-blacklist'
      ? 'Delete and Blacklist'
      : 'Delete and Retain';
  };

  const onConfirm = () => {
    setLoading(true);

    let payload: { assets: string[] };
    let endpoint: string;

    if (props.action === 'delete-related-names') {
      payload = { assets: props.assets };
      endpoint = 'settings/asset-management/seed-data/related-names/delete/';
    } else if (props.action === 'delete-and-blacklist') {
      payload = { assets: props.assets };
      endpoint = 'settings/asset-management/seed-data/key-domains/delete/';
    } else {
      // delete-and-retain
      payload = { assets: props.assets };
      endpoint = 'settings/asset-management/seed-data/key-domains/remove/';
    }

    RestApi.setData(endpoint, payload, 'POST').subscribe(
      (response: any) => {
        // Update grid with the response results
        if (response && response.results !== undefined) {
          dispatch(replaceAllRows({ rows: response.results, count: response.count }));
        }

        let successMessage: string;
        if (props.action === 'delete-related-names') {
          successMessage = 'Related names deleted successfully';
        } else {
          const actionText = props.action === 'delete-and-blacklist'
            ? 'deleted and blacklisted'
            : 'deleted and retained';
          successMessage = `Key domains ${actionText} successfully`;
        }

        dispatch(showSuccess(successMessage));
        setLoading(false);
        props.onClose();
      },
      (error: any) => {
        const errorMessage = extractErrorMessage(error, 'Failed to process assets. Please try again.');
        dispatch(showError(errorMessage));
        setLoading(false);
        props.onClose();
      }
    );
  };

  return (
    <div className="KeyDomainsDeletePopup popup-inner-wrapper">
      <PopupHeader text={getTitle()} onClose={props.onClose} />
      <div className="popup-inner">
        <div className="message">
          <div>{getMessage()}</div>
        </div>

        <div className="assets-list">
          <label>Assets to be processed:</label>
          <div className="assets-container">
            {props.assets.map((asset: string) => (
              <div key={asset} className="asset-item">{asset}</div>
            ))}
          </div>
        </div>

        <div className="buttons-wrapper">
          <button className="btn btn-big btn-secondary" onClick={props.onClose}>
            Cancel
          </button>
          <button
            className={classNames('btn', 'btn-big', 'btn-main')}
            onClick={onConfirm}
            disabled={loading}
          >
            {getButtonText()}
          </button>
        </div>
      </div>
      {loading && <AppLoading />}
    </div>
  );
}
